import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ViralClipsDashboardRow from '../components/ViralClipsDashboardRow';
import './css/ViralClipsDashboardDemo.css';

const ViralClipsDashboardDemo = () => {
  const { videoId, chunkId = '0' } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clipsData, setClipsData] = useState(null);
  const [isEnhancedView, setIsEnhancedView] = useState(!!videoId);
  
  // These would be your actual clip video URLs in a real implementation
  // Map clip IDs to their video URLs
  const demoVideoUrls = {
    original: 'https://example.com/original-video.mp4',
    clip1: 'https://example.com/clip1.mp4',
    clip2: 'https://example.com/clip2.mp4',
    clip3: 'https://example.com/clip3.mp4',
    clip4: 'https://example.com/clip4.mp4',
    clip5: 'https://example.com/clip5.mp4',
    clip6: 'https://example.com/clip6.mp4',
    clip7: 'https://example.com/clip7.mp4',
    clip8: 'https://example.com/clip8.mp4',
  };

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setLoading(true);
        
        let response;
        
        // Check if we're in enhanced mode (using videoId and chunkId) or legacy mode (using jobId)
        if (videoId) {
          // Enhanced clips metadata API
          response = await fetch(`https://app.polarcut.io/api/clips-meta/enhanced/${videoId}/${chunkId}`);
          setIsEnhancedView(true);
        } else {
          // Legacy viral clips metadata API (fallback)
          const jobId = new URLSearchParams(window.location.search).get('jobId') || 'Vr4T6iCLWCg';
          response = await fetch(`https://app.polarcut.io/api/clips-meta/viral/${jobId}`);
          setIsEnhancedView(false);
        }
        
        if (!response.ok) {
          throw new Error(`Failed to fetch metadata: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        setClipsData(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching clips metadata:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchMetadata();
  }, [videoId, chunkId]);

  if (loading) {
    return (
      <div className="viral-clips-dashboard-demo loading">
        <div className="loader"></div>
        <p>Loading clips metadata...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="viral-clips-dashboard-demo error">
        <h2>Error Loading Metadata</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  // Extract model or processing information for header
  const modelInfo = clipsData?.model_information || clipsData?.processing_metadata || null;
  const processedAt = clipsData?.processed_at || clipsData?.enhanced_at || clipsData?.processing_metadata?.processed_at;

  return (
    <div className="viral-clips-dashboard-demo">
      <div className="demo-header">
        <h1>{isEnhancedView ? 'Enhanced' : 'Viral'} Clips Dashboard</h1>
        <div className="job-info">
          {isEnhancedView ? (
            <>
              <span>Video ID: {videoId || clipsData?.video_id || 'N/A'}</span>
              <span>Chunk ID: {chunkId || clipsData?.chunk_id || 'N/A'}</span>
            </>
          ) : (
            <span>Job ID: {clipsData?.source_file?.replace('.json', '') || 'N/A'}</span>
          )}
          {modelInfo && (
            <span>Model: {modelInfo.model_id || modelInfo.region || 'N/A'}</span>
          )}
          <span>Processed: {processedAt ? new Date(processedAt).toLocaleString() : 'N/A'}</span>
        </div>
      </div>
      
      {clipsData ? (
        <ViralClipsDashboardRow 
          clipsData={clipsData} 
          videoUrls={demoVideoUrls} 
          isEnhanced={isEnhancedView}
        />
      ) : (
        <div className="no-data">
          <p>No clips metadata available.</p>
        </div>
      )}
    </div>
  );
};

export default ViralClipsDashboardDemo; 